import React, { useState, useEffect, useMemo, ReactNode } from 'react';
import { 
  Container, Typography, Box, Paper, Grid, Card, CardContent, 
  CardMedia, Button, Chip, Divider, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Tabs, Tab, Popover, List, ListItem, ListItemText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEO from '../SEO/SEO';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { styled } from '@mui/material/styles';

// Icons
import ArticleIcon from '@mui/icons-material/Article';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CloseIcon from '@mui/icons-material/Close';

// Types
type LanguageContent = {
  fr: string;
  en: string;
  es: string;
  [key: string]: string; // Index signature pour permettre l'accès dynamique
};

interface BlogPost {
  id: string;
  title: LanguageContent;
  content: LanguageContent;
  summary: LanguageContent;
  category: string;
  author: string;
  createdAt: any;
  updatedAt: any;
  imageUrl?: string; // URL de l'image principale de l'article
}

// Type pour les segments de texte formatés
type TextSegment = {
  text: string;
  highlighted: boolean;
  blueHighlighted?: boolean; // Nouveau type pour le texte entre accolades
  isImage?: boolean; // Nouveau type pour les images
  imageUrl?: string; // URL de l'image si c'est une image
};

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: 'var(--fd-gridBLOG)',
  backdropFilter: 'blur(10px)',
  borderRadius: '20px',
  overflow: 'hidden',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  border: '1px solid rgba(104, 241, 209, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.2)',
  },
}));

// Fonction utilitaire pour formater le texte avec des crochets, accolades et liens d'images en segments colorés
const formatTextWithBrackets = (text: string): TextSegment[] => {
  if (!text) return [];
  
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  
  // Traiter le texte en cherchant les crochets, les accolades et les liens d'images
  // On utilise une approche séquentielle pour traiter le texte caractère par caractère
  let currentText = '';
  let inBracket = false;
  let bracketContent = '';
  let inCurlyBrace = false;
  let curlyBraceContent = '';
  let inImageTag = false;
  let imageContent = '';
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = i < text.length - 1 ? text[i + 1] : '';
    
    // Détection du début d'un tag d'image (#)
    if (char === '#' && !inBracket && !inCurlyBrace && !inImageTag) {
      // Ajouter le texte accumulé jusqu'à présent
      if (currentText) {
        segments.push({
          text: currentText,
          highlighted: false,
          blueHighlighted: false
        });
        currentText = '';
      }
      inImageTag = true;
      continue;
    }
    
    // Détection de la fin d'un tag d'image (#)
    if (char === '#' && inImageTag) {
      // Vérifier si c'est une URL valide
      const urlRegex = /^(https?:\/\/[^\s]+)$/;
      if (urlRegex.test(imageContent)) {
        // C'est une URL d'image, on l'ajoute comme segment d'image
        segments.push({
          text: '',
          highlighted: false,
          blueHighlighted: false,
          isImage: true,
          imageUrl: imageContent
        });
      } else {
        // Ce n'est pas une URL valide, on l'ajoute comme texte normal
        segments.push({
          text: '#' + imageContent + '#',
          highlighted: false,
          blueHighlighted: false
        });
      }
      imageContent = '';
      inImageTag = false;
      continue;
    }
    
    // Détection du début d'un crochet
    if (char === '[' && !inBracket && !inCurlyBrace && !inImageTag) {
      // Ajouter le texte accumulé jusqu'à présent
      if (currentText) {
        segments.push({
          text: currentText,
          highlighted: false,
          blueHighlighted: false
        });
        currentText = '';
      }
      inBracket = true;
      continue;
    }
    
    // Détection de la fin d'un crochet
    if (char === ']' && inBracket) {
      // Ajouter le contenu du crochet
      segments.push({
        text: bracketContent,
        highlighted: true,
        blueHighlighted: false
      });
      bracketContent = '';
      inBracket = false;
      continue;
    }
    
    // Détection du début d'une accolade
    if (char === '{' && !inBracket && !inCurlyBrace && !inImageTag) {
      // Ajouter le texte accumulé jusqu'à présent
      if (currentText) {
        segments.push({
          text: currentText,
          highlighted: false,
          blueHighlighted: false
        });
        currentText = '';
      }
      inCurlyBrace = true;
      continue;
    }
    
    // Détection de la fin d'une accolade
    if (char === '}' && inCurlyBrace) {
      // Ajouter le contenu de l'accolade
      segments.push({
        text: curlyBraceContent,
        highlighted: false,
        blueHighlighted: true
      });
      curlyBraceContent = '';
      inCurlyBrace = false;
      continue;
    }
    
    // Accumulation du contenu
    if (inBracket) {
      bracketContent += char;
    } else if (inCurlyBrace) {
      curlyBraceContent += char;
    } else if (inImageTag) {
      imageContent += char;
    } else {
      currentText += char;
    }
  }
  
  // Ajouter le texte restant
  if (currentText) {
    segments.push({
      text: currentText,
      highlighted: false,
      blueHighlighted: false
    });
  }
  
  // Si on était au milieu d'un tag à la fin du texte, l'ajouter tel quel
  if (inBracket && bracketContent) {
    segments.push({
      text: '[' + bracketContent,
      highlighted: false,
      blueHighlighted: false
    });
  }
  
  if (inCurlyBrace && curlyBraceContent) {
    segments.push({
      text: '{' + curlyBraceContent,
      highlighted: false,
      blueHighlighted: false
    });
  }
  
  if (inImageTag && imageContent) {
    segments.push({
      text: '#' + imageContent,
      highlighted: false,
      blueHighlighted: false
    });
  }
  
  return segments;
};

// État pour le lightbox d'image
interface LightboxState {
  open: boolean;
  imageUrl: string;
}

// Composant pour afficher le texte formaté avec images
const FormattedText: React.FC<{ content: string }> = ({ content }) => {
  const segments = formatTextWithBrackets(content);
  const [lightbox, setLightbox] = useState<LightboxState>({ open: false, imageUrl: '' });
  
  // Fonction pour ouvrir le lightbox
  const handleOpenLightbox = (imageUrl: string) => {
    setLightbox({ open: true, imageUrl });
  };
  
  // Fonction pour fermer le lightbox
  const handleCloseLightbox = () => {
    setLightbox({ open: false, imageUrl: '' });
  };
  
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.isImage && segment.imageUrl) {
          // Afficher une image
          return (
            <Box 
              key={index} 
              sx={{ 
                my: 2,
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                breakInside: 'avoid', // Empêche l'image d'être coupée entre deux colonnes
              }}
            >
              <Box 
                component="img"
                src={segment.imageUrl}
                alt="Image de l'article"
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
                onClick={() => handleOpenLightbox(segment.imageUrl || '')}
              />
            </Box>
          );
        } else {
          // Afficher du texte formaté
          return (
            <span 
              key={index} 
              style={{ 
                color: segment.highlighted 
                  ? 'var(--texteExergueBlog)' 
                  : segment.blueHighlighted 
                    ? 'var(--NotesSourcesBlog)' 
                    : 'inherit' 
              }}
            >
              {segment.text}
            </span>
          );
        }
      })}
      
      {/* Lightbox pour afficher les images en grand */}
      <Dialog
        open={lightbox.open}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        PaperProps={{
          sx: {
            background: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
            maxHeight: '90vh',
            maxWidth: '90vw',
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={lightbox.imageUrl}
            alt="Image agrandie"
            sx={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        </Box>
      </Dialog>
    </>
  );
};

export default function Blog() {
  const { t, i18n } = useTranslation();
  const { userProfile, isAdmin } = useAuth();
  
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPost, setCurrentPost] = useState<Partial<BlogPost> | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | 'view'>('add');
  // Nouvel état pour le popup de résumé
  const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
  const [summaryPost, setSummaryPost] = useState<BlogPost | null>(null);
  
  // États pour le popover d'affichage compact
  const [compactViewAnchorEl, setCompactViewAnchorEl] = useState<HTMLButtonElement | null>(null);
  const openCompactView = Boolean(compactViewAnchorEl);
  const compactViewId = openCompactView ? 'compact-view-popover' : undefined;
  
  // Filtrer et trier les articles
  const filteredPosts = useMemo(() => {
    // Filtrer par catégorie
    let filtered = [...posts];
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(post => post.category === categoryFilter);
    }
    
    // Trier par date
    return filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      
      if (sortBy === 'newest') {
        return dateB.getTime() - dateA.getTime();
      } else {
        return dateA.getTime() - dateB.getTime();
      }
    });
  }, [posts, categoryFilter, sortBy]);
  
  // Fetch blog posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const q = query(collection(db, 'blogPosts'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const fetchedPosts: BlogPost[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedPosts.push({
            id: doc.id,
            title: data.title || { fr: '', en: '', es: '' },
            content: data.content || { fr: '', en: '', es: '' },
            summary: data.summary || { fr: '', en: '', es: '' },
            category: data.category || '',
            author: data.author || '',
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            imageUrl: data.imageUrl || ''
          });
        });
        
        // Pas besoin de filtrer par langue car tous les posts ont maintenant du contenu dans toutes les langues
        setPosts(fetchedPosts);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, [i18n.language]);
  
  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };
  
  // Handle dialog open for adding/editing post
  const handleOpenDialog = (mode: 'add' | 'edit' | 'view', post?: BlogPost) => {
    setDialogMode(mode);
    if (mode === 'add') {
      setCurrentPost({
        title: { fr: '', en: '', es: '' },
        content: { fr: '', en: '', es: '' },
        summary: { fr: '', en: '', es: '' },
        category: 'general',
        author: userProfile?.username || '',
        imageUrl: ''
      });
    } else if (post) {
      // S'assurer que le champ imageUrl est toujours présent, même pour les anciens articles
      setCurrentPost({
        ...post,
        imageUrl: post.imageUrl || ''
      });
    }
    setOpenDialog(true);
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPost(null);
  };

  // Afficher le popup de résumé
  const handleShowSummary = (post: BlogPost, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setSummaryPost(post);
    setOpenSummaryDialog(true);
  };

  // Fermer le popup de résumé
  const handleCloseSummary = () => {
    setOpenSummaryDialog(false);
    setSummaryPost(null);
  };

  // Fonctions pour gérer le popover d'affichage compact
  const handleOpenCompactView = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCompactViewAnchorEl(event.currentTarget);
  };
  
  const handleCloseCompactView = () => {
    setCompactViewAnchorEl(null);
  };
  
  const handleCompactItemClick = (post: BlogPost) => {
    handleCloseCompactView();
    handleViewFullPost(post);
  };

  // Handle view full post
  const handleViewFullPost = (post: BlogPost) => {
    setDialogMode('view');
    setCurrentPost(post);
    setOpenDialog(true);
    // Fermer le popup de résumé s'il est ouvert
    setOpenSummaryDialog(false);
  };
  
  // Handle save post
  const handleSavePost = async () => {
    if (!currentPost) return;
    
    try {
      if (dialogMode === 'add') {
        await addDoc(collection(db, 'blogPosts'), {
          ...currentPost,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        if (currentPost.id) {
          await updateDoc(doc(db, 'blogPosts', currentPost.id), {
            ...currentPost,
            updatedAt: serverTimestamp()
          });
        }
      }
      
      // Refresh posts
      const q = query(collection(db, 'blogPosts'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const fetchedPosts: BlogPost[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        fetchedPosts.push({
          id: doc.id,
          title: data.title || { fr: '', en: '', es: '' },
          content: data.content || { fr: '', en: '', es: '' },
          summary: data.summary || { fr: '', en: '', es: '' },
          category: data.category || '',
          author: data.author || '',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          imageUrl: data.imageUrl || ''
        });
      });
      
      // Pas besoin de filtrer par langue car tous les posts ont maintenant du contenu dans toutes les langues
      setPosts(fetchedPosts);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving blog post:', error);
    }
  };
  
  // Handle delete post
  const handleDeletePost = async (postId: string) => {
    if (window.confirm(t('blog.deleteConfirmation'))) {
      try {
        await deleteDoc(doc(db, 'blogPosts', postId));
        setPosts(posts.filter(post => post.id !== postId));
      } catch (error) {
        console.error('Error deleting blog post:', error);
      }
    }
  };
  
  return (
  <>
    <SEO 
      title={t('seo.blog.title', 'Blog | LunaNüva')}
      description={t('seo.blog.description', 'Articles et ressources sur l\'interprétation des rêves, la psychologie onirique et les techniques d\'analyse.')}
      keywords={t('seo.blog.keywords', 'blog rêves, articles rêves, psychologie onirique, interprétation')}
    />
    <Box sx={{background: 'var(--Pages-background3)', minHeight: '100vh', display: 'flex',flexDirection: 'column'}}>
    <Container maxWidth="lg" sx={{ mt: 0, mb: 4 }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        mt: { xs: 9, sm: 11, md: 12 },
        mb: { xs: 6, sm: 7, md: 9 }
      }}>
        {/* Conteneur Grid centré : illustration + texte */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0, // Pas d'espacement par défaut pour contrôler le chevauchement
          mt: 0,
          mb: 0,
          width: '100%'
        }}>
          {/* Illustration à gauche */}
          <Box 
            component="img"
            src="/assets/home/blog2.png"
            alt="Blog"
            sx={{
              maxWidth: { xs: '150px', sm: '210px', md: '250px' },
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
          
          {/* Texte à droite avec chevauchement */}
          <Box sx={{
            fontFamily: '"Amatic SC", cursive',
            fontSize: { xs: '2.5rem', sm: '4rem', md: '5rem' },
            fontWeight: { xs: '600', sm: '400', md: '400' },
            color: 'var(--ConnectButton)',            
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            justifySelf: 'start',
            marginLeft: { xs: '-100px', sm: '-120px', md: '-145px' }, // Marge négative pour chevauchement
            zIndex: 2 // Au-dessus de l'illustration
          }}>
            {/* Ligne 1 */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-3deg)',
              transformOrigin: 'left center',
              ml: 10,
              mb: -1
            }}>
              {i18n.language === 'fr' && 'Explorer l\'univers'}
              {i18n.language === 'en' && 'Explore the universe'}
              {i18n.language === 'es' && 'Explorar el universo'}
            </Box>
            
            {/* Ligne 2 */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(3deg)',
              transformOrigin: 'left center',
              ml: 15,
              mb: 0
            }}>
              {i18n.language === 'fr' && 'de l\'interprétation'}
              {i18n.language === 'en' && 'of dream'}
              {i18n.language === 'es' && 'de la interpretación'}
            </Box>
            
            {/* Ligne 3 */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-2deg)',
              transformOrigin: 'left center',
              ml: 10
            }}>
              {i18n.language === 'fr' && 'des rêves'}
              {i18n.language === 'en' && 'interpretation'}
              {i18n.language === 'es' && 'de sueños'}
            </Box>
          </Box>
        </Box>
      </Box>

      

      {/* Admin Controls */}
      {isAdmin && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mb: 0 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
            sx={{
              background: 'linear-gradient(135deg, #68f1d1 0%, #4ac9ab 100%)',
              color: '#1a1b35',
              borderRadius: '30px',
              px: 3,
              boxShadow: '0 8px 25px rgba(10, 10, 10, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4ac9ab 0%, #68f1d1 100%)',
                boxShadow: '0 8px 25px rgba(10, 10, 10, 0.6)',
              },
            }}
          >
            {t('blog.addPost')}
          </Button>
        </Box>
      )}

      {/* Featured Post */}
      {!loading && posts.length > 0 && (
        <Box sx={{ width: '100%', mb: 6, position: 'relative' }}>
            {/* Badge "Nouveau" */}
            <Box
              sx={{
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                background: 'var(--cancel-button-gradient)',
                color: 'white',
                fontWeight: 'bold',
                padding: '10px 16px',
                borderRadius: '20px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              {t('blog.new')}
            </Box>
            <StyledCard 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                height: 'auto',
                cursor: 'pointer',
                p: { xs: 3, sm: 4 },
                gap: { xs: 3, sm: 4 }
              }}
              onClick={() => handleViewFullPost(posts[0])}
            >
              {/* Image à gauche (ou en haut en mode xs) */}
              {posts[0].imageUrl && (
                <Box 
                  sx={{ 
                    width: { xs: '100%', sm: '30%' },
                    minWidth: { sm: '200px' },
                    maxWidth: { sm: '300px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box 
                    component="img"
                    src={posts[0].imageUrl}
                    alt={posts[0].title?.[i18n.language] || ''}
                    sx={{
                      width: '100%',
                      maxHeight: { xs: '200px', sm: '250px' },
                      objectFit: 'cover',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                </Box>
              )}
              
              {/* Contenu à droite (ou en bas en mode xs) */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                    {isAdmin && (
                      <Box>
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog('edit', posts[0]);
                          }}
                          sx={{ color: '#68f1d1' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePost(posts[0].id);
                          }}
                          sx={{ color: '#FF4B8C' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', mb: 2, fontFamily: 'Comfortaa', fontSize: { xs: '1.5rem', sm: '1.5rem', md: '2rem' } }}>
                    {posts[0].title?.[i18n.language] || ''}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--texteExergueBlog)', opacity: 0.8, mb: 3 }}>
                    {posts[0].summary?.[i18n.language] || ''}
                  </Typography>
                </Box>
                <Box sx={{ mt: 'auto' }}>
                  <Divider sx={{ borderColor: 'rgba(104, 241, 209, 0.2)', mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip 
                        label={t(`blog.categories.${posts[0].category}`)} 
                        size="small" 
                        sx={{ 
                          background: 'linear-gradient(135deg, #FF4B8C 0%, #FF8C42 100%)',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: '24px'
                        }} 
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#68f1d1', fontSize: '0.9rem' }} />
                        <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                          {posts[0].author}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon sx={{ color: '#68f1d1', fontSize: '0.9rem' }} />
                      <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                        {formatDate(posts[0].createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </StyledCard>
          </Box>
        )}

        {/* Filter Controls */}
        <Box sx={{ width: '100%', mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant={categoryFilter === 'all' ? 'contained' : 'outlined'}
              onClick={() => setCategoryFilter('all')}
              sx={{ 
                borderColor: 'rgba(104, 241, 209, 0.5)',
                color: categoryFilter === 'all' ? 'black' : '#68f1d1',
                bgcolor: categoryFilter === 'all' ? '#68f1d1' : 'transparent',
                '&:hover': { borderColor: '#68f1d1', bgcolor: categoryFilter === 'all' ? '#68f1d1' : 'rgba(104, 241, 209, 0.1)' }
              }}
            >
              {t('common.all')}
            </Button>
            {['general', 'dreams', 'psychology', 'spirituality', 'science', 'series', 'analyst'].map(category => (
              <Button 
                key={category}
                variant={categoryFilter === category ? 'contained' : 'outlined'}
                onClick={() => setCategoryFilter(category)}
                sx={{ 
                  borderColor: 'rgba(104, 241, 209, 0.5)',
                  color: categoryFilter === category ? 'black' : '#68f1d1',
                  bgcolor: categoryFilter === category ? '#68f1d1' : 'transparent',
                  '&:hover': { borderColor: '#68f1d1', bgcolor: categoryFilter === category ? '#68f1d1' : 'rgba(104, 241, 209, 0.1)' }
                }}
              >
                {t(`blog.categories.${category}`)}
              </Button>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant={sortBy === 'newest' ? 'contained' : 'outlined'}
              onClick={() => setSortBy('newest')}
              sx={{ 
                borderColor: 'rgba(104, 241, 209, 0.5)',
                color: sortBy === 'newest' ? 'black' : '#68f1d1',
                bgcolor: sortBy === 'newest' ? '#68f1d1' : 'transparent',
                '&:hover': { borderColor: '#68f1d1', bgcolor: sortBy === 'newest' ? '#68f1d1' : 'rgba(104, 241, 209, 0.1)' }
              }}
            >
              {t('blog.newest')}
            </Button>
            <Button 
              variant={sortBy === 'oldest' ? 'contained' : 'outlined'}
              onClick={() => setSortBy('oldest')}
              sx={{ 
                borderColor: 'rgba(104, 241, 209, 0.5)',
                color: sortBy === 'oldest' ? 'black' : '#68f1d1',
                bgcolor: sortBy === 'oldest' ? '#68f1d1' : 'transparent',
                '&:hover': { borderColor: '#68f1d1', bgcolor: sortBy === 'oldest' ? '#68f1d1' : 'rgba(104, 241, 209, 0.1)' }
              }}
            >
              {t('blog.oldest')}
            </Button>
            <Button
              aria-describedby={compactViewId}
              variant="contained"
              onClick={handleOpenCompactView}
              sx={{ 
                background: 'var(--stat-progress2)',
                color: 'white',
                border: 'none',
                '&:hover': { 
                  opacity: 0.9,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                }
              }}
            >
              {t('blog.compactView')}
            </Button>
          </Box>
        </Box>

        {/* Popover pour l'affichage compact */}
        <Popover
          id={compactViewId}
          open={openCompactView}
          anchorEl={compactViewAnchorEl}
          onClose={handleCloseCompactView}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: '800px' },
              maxWidth: '90vw',
              maxHeight: '500px',
              overflow: 'auto',
              background: 'var(--fd-gridBLOG)',
              backdropFilter: 'blur(10px)',
              border: '2px solid var(--dark-green)',
              borderRadius: '8px',
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: 'rgba(0, 0, 0, 0.1)', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb': { background: 'linear-gradient(180deg, #68f1d1 0%, #3ab7bf 100%)', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'linear-gradient(180deg, #3ab7bf 0%, #68f1d1 100%)' }
            }
          }}
        >
          <List sx={{ p: 0 }}>
            {filteredPosts.map((post) => (
              <ListItem 
                key={post.id} 
                onClick={() => handleCompactItemClick(post)}
                divider
                sx={{
                  borderBottom: '1px solid rgba(104, 241, 209, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(104, 241, 209, 0.1)'
                  },
                  cursor: 'pointer'
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={t(`blog.categories.${post.category}`)} 
                        size="small" 
                        sx={{ 
                          background: 'linear-gradient(135deg, #FF4B8C 0%, #FF8C42 100%)',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: '20px',
                          minWidth: '60px'
                        }} 
                      />
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 'normal' }}>
                        {post.title?.[i18n.language] || ''}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: '0.8rem' }} />
                        {post.author}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: '0.8rem' }} />
                        {formatDate(post.createdAt)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Popover>
        
        {/* Blog Posts Grid */}
        {!loading && filteredPosts.length > 0 ? (
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {filteredPosts.map((post: BlogPost) => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <StyledCard 
                  sx={{ 
                    height: 'auto', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer',
                    p: 2,
                    minHeight: '120px'
                  }}
                  onClick={(e) => handleShowSummary(post, e)}
                >
                  <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Chip 
                      label={t(`blog.categories.${post.category}`)} 
                      size="small" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FF4B8C 0%, #FF8C42 100%)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem'
                      }} 
                    />
                    {isAdmin && (
                      <Box>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog('edit', post);
                          }}
                          sx={{ color: '#68f1d1' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePost(post.id);
                          }}
                          sx={{ color: '#FF4B8C' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Affichage de l'image si disponible */}
                  {post.imageUrl && (
                    <Box 
                      sx={{ 
                        width: '100%',
                        height: '160px',
                        mb: 1.5
                      }}
                    >
                      <Box 
                        component="img"
                        src={post.imageUrl}
                        alt={post.title?.[i18n.language] || ''}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                        }}
                      />
                    </Box>
                  )}
                  
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'white', 
                      mb: 1, 
                      fontFamily: 'Comfortaa',
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }, // Taille de police ajustable ici
                      lineHeight: 1.3,
                      flexGrow: 1
                    }}
                  >
                    {post.title?.[i18n.language] || ''}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', flexWrap: 'wrap', gap: 1 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'white', 
                        opacity: 0.7, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        minWidth: '80px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: '0.8rem', flexShrink: 0 }} />
                      {formatDate(post.createdAt)}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'white', 
                        opacity: 0.7, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        minWidth: '80px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <PersonIcon sx={{ fontSize: '0.8rem', flexShrink: 0 }} />
                      {post.author}
                    </Typography>
                  </Box>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        ) : !loading ? (
          <Box sx={{ 
            p: 5, 
            textAlign: 'center',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '20px',
            width: '100%'
          }}>
            <ArticleIcon sx={{ fontSize: '4rem', color: '#68f1d1', opacity: 0.5, mb: 2 }} />
            <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
              {t('blog.noPosts')}
            </Typography>
            <Typography sx={{ color: 'white', opacity: 0.7 }}>
              {t('blog.checkBackLater')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            p: 5, 
            textAlign: 'center',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '20px',
            width: '100%'
          }}>
            <Typography variant="h5" sx={{ color: 'white' }}>
              {t('blog.loading')}
            </Typography>
          </Box>
        )}

      {/* Dialog pour afficher le résumé de l'article */}
      <Dialog 
        open={openSummaryDialog} 
        onClose={handleCloseSummary}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, rgba(45, 46, 73, 0.95) 0%, rgba(35, 36, 63, 0.98) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(104, 241, 209, 0.2)',
            color: 'white',
            p: { xs: 2, sm: 3 },
          }
        }}
      >
        {summaryPost && (
          <>
            <DialogTitle sx={{ p: 0, mb: 2 }}>
              <Typography variant="h5" sx={{ fontFamily: 'Comfortaa', color: 'white' }}>
                {summaryPost.title?.[i18n.language]}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Typography variant="body1" sx={{ color: 'var(--texteExergueBlog)', mb: 3 }}>
                {summaryPost.summary?.[i18n.language]}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {formatDate(summaryPost.createdAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {summaryPost.author}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 0, mt: 3 }}>
              <Button 
                onClick={handleCloseSummary}
                sx={{ 
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                variant="outlined"
              >
                {t('common.close')}
              </Button>
              <Button 
                onClick={() => handleViewFullPost(summaryPost)}
                sx={{ 
                  background: 'linear-gradient(135deg, #68f1d1 0%, #4ac9ab 100%)',
                  color: '#1a1b35',
                  '&:hover': { background: 'linear-gradient(135deg, #4ac9ab 0%, #68f1d1 100%)' }
                }}
                variant="contained"
              >
                {t('blog.readArticle')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog for adding/editing/viewing posts */}
      <Dialog 
        open={openDialog} 
        onClose={dialogMode === 'view' ? handleCloseDialog : undefined}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--fond-Dialog)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(104, 241, 209, 0.2)',
            color: 'white',
            p: dialogMode === 'view' ? 0 : { xs: 2, sm: 3 },
            maxHeight: '90vh',
            overflow: 'hidden',
            ...(dialogMode === 'view' && {
              display: 'flex',
              flexDirection: 'column'
            })
          }
        }}
      >
        {dialogMode === 'view' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* En-tête avec bouton de fermeture et d'édition */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid rgba(104, 241, 209, 0.2)'
            }}>
              <Chip 
                label={t(`blog.categories.${currentPost?.category}`)} 
                sx={{ 
                  background: 'var(--anxiety-high)',
                  color: 'white'
                }} 
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isAdmin && (
                  <IconButton 
                    onClick={() => handleOpenDialog('edit', currentPost as BlogPost)}
                    sx={{ color: '#68f1d1' }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                <IconButton 
                  onClick={handleCloseDialog}
                  sx={{ color: 'white' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            
            {/* Contenu de l'article avec un seul ascenseur */}
            <Box sx={{ 
              p: 3, 
              overflow: 'auto',
              flexGrow: 1,
              maxHeight: 'calc(90vh - 90px)', // Ajusté pour tenir compte du footer
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: 'rgba(0, 0, 0, 0.1)', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb': { background: 'linear-gradient(180deg, #68f1d1 0%, #3ab7bf 100%)', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'linear-gradient(180deg, #3ab7bf 0%, #68f1d1 100%)' }
            }}>
              {/* En-tête avec image et titre */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 3,
                gap: 3
              }}>
                {/* Image principale si disponible - colonne de gauche (1/3) */}
                {currentPost?.imageUrl && (
                  <Box 
                    sx={{ 
                      width: { xs: '100%', sm: '33%' },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Box 
                      component="img"
                      src={currentPost.imageUrl}
                      alt={currentPost?.title?.[i18n.language] || ''}
                      sx={{
                        width: '100%',
                        maxHeight: { xs: '200px', sm: '300px' },
                        objectFit: 'cover',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                      }}
                    />
                  </Box>
                )}
                
                {/* Titre - colonne de droite (2/3) ou pleine largeur si pas d'image */}
                <Box sx={{ 
                  width: currentPost?.imageUrl ? { xs: '100%', sm: '67%' } : '100%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      color: 'white', 
                      fontFamily: 'Comfortaa',
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}
                  >
                    {currentPost?.title?.[i18n.language] || ''}
                  </Typography>
                </Box>
              </Box>
              
              {/* Métadonnées */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ color: 'white', opacity: 0.7, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: '0.9rem' }} />
                  {currentPost?.author}
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', opacity: 0.7, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: '0.9rem' }} />
                  {formatDate(currentPost?.createdAt)}
                </Typography>
              </Box>
              
              {/* Résumé */}
              <Typography variant="body1" sx={{ color: 'var(--texteExergueBlog)', opacity: 0.8, mb: 3, fontStyle: 'italic' }}>
                {currentPost?.summary?.[i18n.language] || ''}
              </Typography>
              
              {/* Contenu */}
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.8,
                  columnCount: { xs: 1, sm: 2 },
                  columnGap: '2rem',
                  columnRuleWidth: '1px',
                  columnRuleStyle: 'solid',
                  columnRuleColor: 'rgba(104, 241, 209, 0.3)',
                  textAlign: 'left',
                  mb: 2 // Petite marge en bas du texte
                }}
              >
                <FormattedText content={currentPost?.content?.[i18n.language] || ''} />
              </Typography>
            </Box>
            
            {/* Footer fixe */}
            <Box sx={{ 
              height: '30px', 
              borderTop: '1px solid rgba(104, 241, 209, 0.3)',
              backgroundColor: 'rgba(35, 36, 63, 1)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10
            }}>
              {/*<Box sx={{ width: '50%', height: '1px', backgroundColor: 'rgba(104, 241, 209, 0.5)' }} />*/}
            </Box>
          </Box>
        ) : (
          <>
            <DialogTitle sx={{ 
              background: 'var(--fd-gris)',
              color: 'white',
              fontFamily: 'Comfortaa',
              fontSize: { xs: '1.3rem', sm: '1.8rem' }
            }}>
              {dialogMode === 'add' ? t('blog.addPost') : t('blog.editPost')}
            </DialogTitle>
            <DialogContent sx={{ 
              background: 'var(--fd-gris)',
              p: { xs: 2, sm: 3 },
              overflow: 'auto',
              maxHeight: { xs: 'calc(100vh - 130px)', sm: 'calc(95vh - 130px)' },
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(180deg, #68f1d1 0%, #3ab7bf 100%)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'linear-gradient(180deg, #3ab7bf 0%, #68f1d1 100%)',
              }
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0 }}>
              {/* Onglets pour les langues */}
              <Box sx={{ mb: 1, mt: -2 }}>
                <Tabs 
                  value={i18n.language} 
                  onChange={(e, newValue) => i18n.changeLanguage(newValue)}
                  sx={{ 
                    '& .MuiTabs-indicator': { backgroundColor: '#68f1d1' },
                    '& .MuiTab-root': { color: 'white', opacity: 0.7 },
                    '& .Mui-selected': { color: '#68f1d1', opacity: 1 }
                  }}
                >
                  <Tab value="fr" label="Français" />
                  <Tab value="en" label="English" />
                  <Tab value="es" label="Español" />
                </Tabs>
              </Box>

              <Typography variant="h6" sx={{ color: '#68f1d1', mb: 2 }}>
                {i18n.language === 'fr' ? 'Français' : i18n.language === 'en' ? 'English' : 'Español'}
              </Typography>

              {/* Champ URL d'image (commun à toutes les langues) */}
              {i18n.language === 'fr' && (
                <TextField
                  fullWidth
                  label="URL de l'image principale"
                  value={currentPost?.imageUrl || ''}
                  onChange={(e) => {
                    if (currentPost) {
                      setCurrentPost({
                        ...currentPost,
                        imageUrl: e.target.value
                      });
                    }
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(104, 241, 209, 0.5)',
                      },
                      '&:hover fieldset': {
                        borderColor: '#68f1d1',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#68f1d1',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#68f1d1',
                    },
                    '& .MuiInputBase-input': {
                      color: 'white',
                    },
                  }}
                />
              )}

              <TextField
                label={t('blog.form.title')}
                fullWidth
                value={currentPost?.title?.[i18n.language] || ''}
                onChange={(e) => {
                  const updatedTitle = { ...currentPost?.title, [i18n.language]: e.target.value };
                  setCurrentPost({ ...currentPost, title: updatedTitle } as BlogPost);
                }}
                InputLabelProps={{ sx: { color: '#68f1d1' } }}
                sx={{ 
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(104, 241, 209, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(104, 241, 209, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#68f1d1' },
                  },
                  '& .MuiInputBase-input': { color: 'white', p: 2 }
                }}
              />
              <TextField
                label={t('blog.form.summary')}
                fullWidth
                multiline
                rows={4}
                maxRows={10}
                value={currentPost?.summary?.[i18n.language] || ''}
                onChange={(e) => {
                  const updatedSummary = { ...currentPost?.summary, [i18n.language]: e.target.value };
                  setCurrentPost({ ...currentPost, summary: updatedSummary } as BlogPost);
                }}
                InputLabelProps={{ sx: { color: '#68f1d1' } }}
                sx={{ 
                  mb: 1,                  
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(104, 241, 209, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(104, 241, 209, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#68f1d1' },
                    resize: 'vertical',
                    minHeight: '100px',
                  },
                  '& .MuiInputBase-input': { 
                    p: 1,
                    color: 'white',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'linear-gradient(180deg, #68f1d1 0%, #3ab7bf 100%)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: 'linear-gradient(180deg, #3ab7bf 0%, #68f1d1 100%)',
                    }
                  }
                }}
              />
              <TextField
                label={t('blog.form.content')}
                fullWidth
                multiline
                rows={29}
                maxRows={30}
                value={currentPost?.content?.[i18n.language] || ''}
                onChange={(e) => {
                  const updatedContent = { ...currentPost?.content, [i18n.language]: e.target.value };
                  setCurrentPost({ ...currentPost, content: updatedContent } as BlogPost);
                }}
                InputLabelProps={{ sx: { color: '#68f1d1' } }}
                sx={{ 
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(104, 241, 209, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(104, 241, 209, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#68f1d1' },
                    resize: 'vertical',
                    minHeight: '300px',
                  },
                  '& .MuiInputBase-input': { 
                    p: 1,
                    color: 'white',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'linear-gradient(180deg, #68f1d1 0%, #3ab7bf 100%)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: 'linear-gradient(180deg, #3ab7bf 0%, #68f1d1 100%)',
                    }
                  }
                }}
              />

              <FormControl fullWidth>
                <InputLabel id="category-label" sx={{ color: '#68f1d1' }}>{t('blog.form.category')}</InputLabel>
                <Select
                  labelId="category-label"
                  value={currentPost?.category || 'general'}
                  label={t('blog.form.category')}
                  onChange={(e) => setCurrentPost({ ...currentPost, category: e.target.value } as BlogPost)}
                  sx={{ 
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(104, 241, 209, 0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(104, 241, 209, 0.5)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#68f1d1' },
                    '& .MuiSvgIcon-root': { color: '#68f1d1' }
                  }}
                >
                  <MenuItem value="general">{t('blog.categories.general')}</MenuItem>
                  <MenuItem value="dreams">{t('blog.categories.dreams')}</MenuItem>
                  <MenuItem value="psychology">{t('blog.categories.psychology')}</MenuItem>
                  <MenuItem value="spirituality">{t('blog.categories.spirituality')}</MenuItem>
                  <MenuItem value="science">{t('blog.categories.science')}</MenuItem>
                  <MenuItem value="series">{t('blog.categories.series')}</MenuItem>
                  <MenuItem value="analyst">{t('blog.categories.analyst')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            </DialogContent>
            <DialogActions sx={{ 
              background: 'linear-gradient(135deg, rgba(45, 46, 73, 0.95) 0%, rgba(35, 36, 63, 0.95) 100%)',
              p: 2
            }}>
              <Button onClick={handleCloseDialog} sx={{ color: 'white' }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSavePost} 
                sx={{ 
                  background: 'linear-gradient(135deg, #68f1d1 0%, #3ab7bf 100%)',
                  color: 'black',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #3ab7bf 0%, #68f1d1 100%)',
                  }
                }}
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  </Box>
  </>
  );
}
